import type { Exam } from '@/types/exam';

/**
 * Where a paper stands, worked out from the facts the API already sends.
 *
 * Only the draft is stored — `publication`, because whether the class has been
 * told of a paper is a decision, not something the marks can show. Everything
 * after that is read off the two things that are true by construction: the date
 * it was sat, and how much of the roster has a mark or an absence recorded. A
 * stored "marked" flag would be a second answer free to disagree with the marks.
 *
 * Read in this order, because each rule only holds once the one above it has
 * failed:
 *
 * 0. **Suspended** — withdrawn by its teacher. Nothing else about it matters
 *    any more: the class no longer sees it and its sheet is closed.
 * 1. **Draft** — not published yet. Only its author and the office see it, and
 *    no marks can be entered against it.
 * 2. **Marked** — every child on the roster is accounted for. The sheet is
 *    finished, so the averages beside it mean the whole class.
 * 3. **Scheduled** — nothing is entered and the paper is not over yet. "Over"
 *    means its date *and* its end time have passed (`hasExamEnded`): a paper
 *    dated today that runs until 12:30 is still scheduled at 10:00.
 * 4. **Marking** — everything else: sat and part-marked, or sat and untouched.
 *
 * A section with nobody in it can never satisfy rule 2 (`0 >= 0` would make
 * every empty class read as finished), which is what the roster guard is for.
 */
export type ExamStatus = 'SUSPENDED' | 'DRAFT' | 'MARKED' | 'MARKING' | 'SCHEDULED';

/**
 * Whether the paper is over, against `now` from `instituteNow()`.
 *
 * The same rule the server applies before it accepts marks: the end time
 * decides; a paper with only a start time is over once it starts, and one with
 * neither is over from the start of its day.
 */
export function hasExamEnded(
  exam: Pick<Exam, 'examDate' | 'startTime' | 'endTime'>,
  now: string,
): boolean {
  return examEndsAt(exam) <= now;
}

/** When the paper is over, as `YYYY-MM-DD HH:MM` on the institute's clock. */
export function examEndsAt(exam: Pick<Exam, 'examDate' | 'startTime' | 'endTime'>): string {
  return `${exam.examDate} ${exam.endTime ?? exam.startTime ?? '00:00'}`;
}

export function examStatus(
  exam: Pick<
    Exam,
    'suspendedAt' | 'publication' | 'enteredCount' | 'totalStudents' | 'examDate' | 'startTime' | 'endTime'
  >,
  now: string,
): ExamStatus {
  if (exam.suspendedAt !== null) return 'SUSPENDED';
  if (exam.publication === 'DRAFT') return 'DRAFT';
  if (exam.totalStudents > 0 && exam.enteredCount >= exam.totalStudents) return 'MARKED';
  if (exam.enteredCount === 0 && !hasExamEnded(exam, now)) return 'SCHEDULED';

  return 'MARKING';
}

export const EXAM_STATUS_LABEL = {
  SUSPENDED: 'Suspended',
  DRAFT: 'Draft',
  MARKED: 'Fully marked',
  MARKING: 'Marks pending',
  SCHEDULED: 'Scheduled',
} as const satisfies Record<ExamStatus, string>;

/** What each standing means, for the tab strip's title and the empty state. */
export const EXAM_STATUS_HINT = {
  SUSPENDED: 'Withdrawn — the class no longer sees it. Marks already entered are kept.',
  DRAFT: 'Not published yet — the class cannot see it.',
  MARKED: 'Every child on the roster is marked or recorded absent.',
  MARKING: 'Sat, but the sheet is not finished.',
  SCHEDULED: 'Not over yet — marks open once its date and end time have passed.',
} as const satisfies Record<ExamStatus, string>;

/**
 * The dot beside the word, never instead of it.
 *
 * Spelled out as literal utilities because Tailwind scans the source for whole
 * class names, and written as palette shades rather than the product's `var()`
 * tokens so they read the same in both themes without a `dark:` prefix.
 */
export const EXAM_STATUS_DOT = {
  SUSPENDED: 'bg-rose-500',
  DRAFT: 'bg-slate-400',
  MARKED: 'bg-emerald-500',
  MARKING: 'bg-blue-500',
  SCHEDULED: 'bg-amber-500',
} as const satisfies Record<ExamStatus, string>;

export const EXAM_STATUS_TEXT = {
  SUSPENDED: 'text-rose-600 dark:text-rose-400',
  DRAFT: 'text-ink-3',
  MARKED: 'text-emerald-600 dark:text-emerald-400',
  MARKING: 'text-blue-600 dark:text-blue-400',
  SCHEDULED: 'text-amber-600 dark:text-amber-400',
} as const satisfies Record<ExamStatus, string>;

/**
 * Whether a paper has dropped off the current list.
 *
 * Sat before today and every child accounted for — nothing is left to do on it,
 * and a year of finished papers would otherwise bury the ones that still need
 * a mark. A past paper still short of marks, or still a draft, stays current
 * until it is dealt with.
 */
export function isPastPaper(exam: Pick<Exam, 'examDate'>, status: ExamStatus, today: string) {
  return status === 'MARKED' && exam.examDate < today;
}

/**
 * The list can be narrowed to one standing, to the current papers, or to the
 * past ones. "Active" (key `CURRENT`, kept so saved URLs still resolve) and
 * "Past papers" split the live list between them; the standings cut across
 * both. "Active" is a roll-up of Drafts, Scheduled and Marks pending, plus a
 * paper fully marked today — it is not a time window, so it must not be called
 * "Current", which reads as "running now".
 */
export type ExamStatusFilter = 'CURRENT' | 'PAST' | ExamStatus;

/** Suspended last, and outside "Active": a withdrawn paper is not one of the live ones. */
export const EXAM_STATUS_FILTERS = [
  'CURRENT',
  'MARKED',
  'MARKING',
  'SCHEDULED',
  'DRAFT',
  'PAST',
  'SUSPENDED',
] as const satisfies readonly ExamStatusFilter[];

export const EXAM_STATUS_TAB_LABEL = {
  CURRENT: 'Active',
  MARKED: 'Fully marked',
  MARKING: 'Marks pending',
  SCHEDULED: 'Scheduled',
  DRAFT: 'Drafts',
  PAST: 'Past papers',
  SUSPENDED: 'Suspended',
} as const satisfies Record<ExamStatusFilter, string>;

/** What each tab holds, for its empty state. */
export const EXAM_STATUS_TAB_HINT = {
  ...EXAM_STATUS_HINT,
  CURRENT:
    'Drafts, scheduled papers and papers still being marked — everything not yet in Past papers.',
  PAST: 'Sat before today and fully marked.',
} as const satisfies Record<ExamStatusFilter, string>;

/* -------------------------------------------------------------------------- */
/* One child's line on the sheet                                              */
/* -------------------------------------------------------------------------- */

/**
 * Where one child stands on a paper.
 *
 * A separate vocabulary from the paper's own standing above, and deliberately
 * so: a paper is "published" because the whole roster is accounted for, while a
 * child is "submitted" because *their* mark is in. They are kept in one file
 * because the sheet prints both within a few inches of each other, and two
 * files is how "Absent" ends up amber in one place and rose in the other.
 *
 * **Absent is not a zero.** A child who sat no paper has no mark, and the two
 * are held apart everywhere — in the tally, in the average, and here.
 */
export type MarkStatus = 'SUBMITTED' | 'ABSENT' | 'PENDING';

export function markStatus(entry: { marks: number | null; isAbsent: boolean }): MarkStatus {
  if (entry.isAbsent) return 'ABSENT';

  return entry.marks === null ? 'PENDING' : 'SUBMITTED';
}

export const MARK_STATUS_LABEL = {
  SUBMITTED: 'Submitted',
  ABSENT: 'Absent',
  PENDING: 'Pending',
} as const satisfies Record<MarkStatus, string>;

export const MARK_STATUS_DOT = {
  SUBMITTED: 'bg-emerald-500',
  ABSENT: 'bg-amber-500',
  PENDING: 'bg-slate-400',
} as const satisfies Record<MarkStatus, string>;

export const MARK_STATUS_TEXT = {
  SUBMITTED: 'text-emerald-600 dark:text-emerald-400',
  ABSENT: 'text-amber-600 dark:text-amber-400',
  PENDING: 'text-ink-3',
} as const satisfies Record<MarkStatus, string>;
